import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Card,
  CardContent,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Chip,
  ImageList,
  ImageListItem,
  Fade,
  Stack,
  IconButton,
  Snackbar,
} from '@mui/material';
import { MetaConsentBanner, MetaConsentManageLink, useEventMetaPixel } from '../../features/meta-pixel';
import { 
  CalendarToday, 
  LocationOn, 
  AccessTime, 
  ExpandMore,
  DirectionsBus,
  Share,
  Facebook,
  Twitter,
  WhatsApp,
  EventSeat,
  PlayArrow,
  Pause,
  ArrowBackIos,
  ArrowForwardIos,
  Add,
  Remove,
} from '@mui/icons-material';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { eventService } from '../../services/eventService';
import { replaceWithExclusiveDomain } from '../../utils/exclusiveDomainRedirect';
import { toBrowserImageUrl } from '../../utils/displayImageUrl';
import { googleMapsEmbedSrc } from '../../utils/googleMapsEmbed';
import { shouldShowEventCategoryTag } from '../../utils/eventCategory';
import { getBrandConfig, resolveBrandKey } from '../../brands/registry';
import { platformBrand } from '../../brands/platform';
import { differenceInCalendarDays, differenceInMinutes, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { alpha } from '@mui/material/styles';
import ActiveLotBanner from '../../components/events/ActiveLotBanner';
import { getTicketLotName } from '../../components/events/lotDisplayUtils';
import type { ActiveSalesPhaseInfo } from '../../components/events/lotDisplayUtils';
import { getSafeTicketDescription } from '../../utils/ticketDisplay';
import { calculateTicketTotalsWithFee } from '../../utils/money';
import { usePlatformChrome } from '../../context/PlatformChromeContext';
import TicketCartDrawer from './components/TicketCartDrawer';
import { EventTicketCards, type PublicTicket } from './components/EventPublicTicketShop';
import CancellationTicketDialog from '../../components/events/CancellationTicketDialog';
import {
  addTicketToCart,
  cartPersonCount,
  getTicketOrderLimits,
  loadTicketCart,
  MAX_CART_ATTENDEES,
  removeTicketFromCart,
  setTicketCartQuantity,
  type TicketCartItem,
} from '../../utils/ticketCart';

function safeAlpha(color: string | undefined | null, value: number, fallback = '#000000'): string {
  try {
    if (!color) return alpha(fallback, value);
    return alpha(color, value);
  } catch {
    return alpha(fallback, value);
  }
}

// Interface para o evento
interface EventData {
  _id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  location: string;
  address: string;
  city: string;
  state: string;
  coverImage?: string;
  galleryImages?: string[];
  isPaid: boolean;
  capacity: number;
  showRemainingSpots?: boolean;
  remainingSpots?: number | null;
  registeredCount?: number;
  category: string;
  parishName?: string;
  parishSlug?: string;
  parishLogoUrl?: string;
  brandKey?: 'platform' | 'zuadaria';
  tickets: TicketData[];
  customFields?: CustomFieldData[];
  products?: ProductData[];
  hasStore?: boolean;
  mapLink?: string;
  status: 'published' | 'paused' | 'test' | 'pre_published';
  isSoldOut?: boolean;
  feePercentage?: number; // Taxa da paróquia em percentual
  pageTemplate?: 'default' | 'acampamento' | 'cinema'; // Template da página pública
  ticketCatalogMode?: 'legacy' | 'batched' | 'phased';
  activeSalesPhase?: ActiveSalesPhaseInfo | null;
  personalizedPage?: boolean;
  customTheme?: {
    primaryColor?: string;
    secondaryColor?: string;
    fontFamily?: string;
  };
  metaPixel?: {
    pixelId?: string;
    enabled?: boolean;
  };
  allowGroupRegistration?: boolean;
  allowInstallments?: boolean;
  slug?: string;
}

// Interface para o ticket
interface TicketData {
  _id: string;
  name: string;
  description: string;
  price: number;
  quantity: number;
  startSaleDate?: string;
  endSaleDate?: string;
  salesPhaseName?: string;
  batchName?: string;
  showPlatformFee?: boolean; // Mostrar taxa da plataforma para este ingresso específico
  allowInstallments?: boolean;
  minPerOrder?: number;
  maxPerOrder?: number | null;
  allowSameAttendeeData?: boolean;
  lastTickets?: boolean;
}

// Interface para campos personalizados
interface CustomFieldData {
  _id: string;
  fieldName: string;
  fieldType: string;
  isRequired: boolean;
  options?: string[];
  eventId?: string;
  order?: number;
}

// Interface para produtos
interface ProductData {
  _id: string;
  name: string;
  description: string;
  price: number;
  quantity: number;
  image?: string;
  startSaleDate?: string;
  endSaleDate?: string;
}

// Componente para exibir a página pública de um evento - Versão Final
const EventPublicPage: React.FC = () => {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [exclusiveDomainUrl, setExclusiveDomainUrl] = useState<string | null>(null);
  const [cartItems, setCartItems] = useState<TicketCartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartNotice, setCartNotice] = useState<string | null>(null);
  const [cancellationTicketOpen, setCancellationTicketOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeImage, setActiveImage] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  
  // Estados para lazy loading de templates (otimização de performance)
  const [TemplateComponent, setTemplateComponent] = useState<React.ComponentType<any> | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const { setNeutralChrome, resetChrome } = usePlatformChrome();
  const brand = useMemo(() => {
    const brandKey = resolveBrandKey({
      publicBrandKey: event?.brandKey,
      legacyLabel: `${event?.parishName || ''} ${event?.parishSlug || ''}`,
    });
    if (brandKey === 'zuadaria') {
      const cfg = getBrandConfig('zuadaria').publicPage;
      return { ...cfg };
    }

    const useCustom =
      !!event?.personalizedPage &&
      !!(event?.customTheme?.primaryColor || event?.customTheme?.secondaryColor);

    if (!useCustom) {
      return { ...platformBrand.publicPage };
    }

    const primary = event!.customTheme!.primaryColor || '#4b1b7a';
    const secondary = event!.customTheme!.secondaryColor || '#6f42a8';
    return {
      pageBg: '#f7f5ff',
      pageBgDeep: '#ebe8ff',
      text: '#1a1040',
      textSoft: '#4f5870',
      heading: primary,
      accent: primary,
      accentSoft: alpha(primary, 0.12),
      border: alpha(primary, 0.2),
      surface: '#ffffff',
      surfaceSoft: '#fbfcff',
      cardShadow: '0 18px 38px rgba(22, 34, 68, 0.14)',
      ctaStart: primary,
      ctaEnd: secondary,
      ctaShadow: alpha(primary, 0.3),
      flatPage: false,
    };
  }, [
    event?.personalizedPage,
    event?.parishName,
    event?.parishSlug,
    event?.brandKey,
    event?.customTheme?.primaryColor,
    event?.customTheme?.secondaryColor,
  ]);

  const organizerDisplayName = useMemo(() => {
    const raw = event?.parishName || (event?.personalizedPage ? 'Organizador' : 'Paróquia Organizadora');
    if (resolveBrandKey({ publicBrandKey: event?.brandKey, legacyLabel: raw }) === 'zuadaria') {
      return getBrandConfig('zuadaria').displayName;
    }
    return raw;
  }, [event?.parishName, event?.personalizedPage, event?.brandKey]);

  const eventDisplayTitle = useMemo(() => {
    const raw = event?.title || '';
    return raw.replace(/azi[\s_-]*adus/gi, 'Zuadaria');
  }, [event?.title]);

  useEffect(() => {
    if (event?.personalizedPage) {
      setNeutralChrome(organizerDisplayName || null);
    } else {
      resetChrome();
    }
    return () => resetChrome();
  }, [event?.personalizedPage, organizerDisplayName, setNeutralChrome, resetChrome]);
  
  const pageTemplate = useMemo(() => event?.pageTemplate || 'default', [event?.pageTemplate]);

  const mapEmbedSrc = useMemo(
    () =>
      googleMapsEmbedSrc(
        event?.mapLink,
        [event?.location, event?.address, event?.city, event?.state].filter(Boolean).join(' ')
      ),
    [event?.mapLink, event?.location, event?.address, event?.city, event?.state]
  );

  const {
    showBanner: showMetaBanner,
    effectivePixel,
    accept: acceptMetaConsent,
    reject: rejectMetaConsent,
    manageCookies: manageMetaCookies,
    trackAddToCart: trackMetaAddToCart,
  } = useEventMetaPixel({
    eventSlug,
    eventId: event?._id,
    eventTitle: event?.title,
    metaPixel: event?.metaPixel,
    trackViewOnMount: Boolean(event),
    disabled: event?.status === 'test',
  });

  const withMetaPixelChrome = (node: React.ReactNode) => (
    <>
      {node}
      {effectivePixel && !showMetaBanner ? (
        <Box sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1300 }}>
          <MetaConsentManageLink onManage={manageMetaCookies} />
        </Box>
      ) : null}
      {showMetaBanner ? (
        <MetaConsentBanner
          onAccept={acceptMetaConsent}
          onReject={rejectMetaConsent}
          brandKey={event?.brandKey}
        />
      ) : null}
    </>
  );

  const eventHasEnded = useMemo(() => {
    if (!event?.endDate) return false;
    return new Date(event.endDate) < new Date();
  }, [event?.endDate]);

  const hasAvailableTickets = useMemo(() => {
    if (!event || !event.tickets || event.tickets.length === 0 || eventHasEnded || event.isSoldOut) {
      return false;
    }

    const now = new Date();

    return event.tickets.some((ticket) => {
      const startSaleDate = ticket.startSaleDate ? new Date(ticket.startSaleDate) : null;
      const endSaleDate = ticket.endSaleDate ? new Date(ticket.endSaleDate) : null;
      const isPhased = event.ticketCatalogMode === 'phased';
      const saleStarted = isPhased || !startSaleDate || startSaleDate <= now;
      const saleNotEnded = !endSaleDate || endSaleDate >= now;
      const hasStock = ticket.quantity > 0;

      return saleStarted && saleNotEnded && hasStock;
    });
  }, [event, eventHasEnded]);

  // No hero/banner mostramos apenas a capa; galeria fica só na seção de galeria
  const displayImages = useMemo(
    () => (event?.coverImage ? [event.coverImage] : []) as string[],
    [event?.coverImage]
  );

  const cartEnabled = !!event?.allowGroupRegistration;
  const isZuadariaPublic = brand.flatPage === true;

  useEffect(() => {
    if (eventSlug) setCartItems(loadTicketCart(eventSlug));
  }, [eventSlug]);

  const handleChangeTicketQty = (ticket: PublicTicket, quantity: number) => {
    if (!eventSlug) return;
    const { min, max } = getTicketOrderLimits(ticket);
    if (quantity <= 0) {
      setCartItems(removeTicketFromCart(eventSlug, String(ticket._id)));
      return;
    }
    const existing = cartItems.find((item) => item.ticketId === String(ticket._id));
    const previousQty = existing?.quantity || 0;
    const clamped = Math.max(min, Math.min(max, quantity));
    const result = existing
      ? setTicketCartQuantity(eventSlug, String(ticket._id), clamped)
      : addTicketToCart(
          eventSlug,
          {
            ticketId: String(ticket._id),
            name: ticket.name,
            price: ticket.price,
            showPlatformFee: ticket.showPlatformFee,
          },
          clamped
        );
    setCartItems(result.items);
    if (result.error) setCartNotice(result.error);
    else if (previousQty === 0 && clamped > 0) {
      trackMetaAddToCart({
        value: (Number(ticket.price) || 0) * clamped,
        numItems: clamped,
        contentIds: [String(ticket._id)],
        contentName: ticket.name,
      });
    }
  };

  const handleAddToCart = (ticket: TicketData) => {
    const current = cartItems.find((item) => item.ticketId === String(ticket._id))?.quantity || 0;
    const { min } = getTicketOrderLimits(ticket);
    handleChangeTicketQty(ticket, current === 0 ? min : current + 1);
  };

  const handleCartQty = (ticketId: string, quantity: number) => {
    if (!eventSlug) return;
    const result = setTicketCartQuantity(eventSlug, ticketId, quantity);
    setCartItems(result.items);
    if (result.error) setCartNotice(result.error);
  };

  const handleRemoveCart = (ticketId: string) => {
    if (!eventSlug) return;
    setCartItems(removeTicketFromCart(eventSlug, ticketId));
  };

  const cartPeople = cartPersonCount(cartItems);
  const cartSubtotal = cartItems.reduce((sum, item) => {
    const line = calculateTicketTotalsWithFee({
      unitPrice: item.price,
      quantity: item.quantity,
      feePercentage: event?.feePercentage,
      showPlatformFee: item.showPlatformFee,
    });
    return sum + line.total;
  }, 0);

  const goToCartCheckout = () => navigate(`/${eventSlug}/inscricao`);

  const withCartChrome = (node: React.ReactNode) => (
    <>
      {node}
      {cartEnabled && (
        <>
          <TicketCartDrawer
            open={cartOpen}
            onClose={() => setCartOpen(false)}
            items={cartItems}
            feePercentage={event?.feePercentage}
            onChangeQuantity={handleCartQty}
            onRemove={handleRemoveCart}
            onContinue={goToCartCheckout}
          />
          <Snackbar
            open={!!cartNotice}
            autoHideDuration={3500}
            onClose={() => setCartNotice(null)}
            message={cartNotice || ''}
          />
        </>
      )}
    </>
  );

  // Buscar dados do evento
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        setError(null);
        setExclusiveDomainUrl(null);

        if (!eventSlug) {
          setError('ID do evento não fornecido.');
          setLoading(false);
          return;
        }

        const ticketFromLink = new URLSearchParams(location.search).get('ticket') || undefined;
        const response = await eventService.getEventBySlug(eventSlug, ticketFromLink);

        if (response.success && response.data) {
          // Extrair parishName do objeto parishId
          const eventData = response.data;
          
          // Debug: verificar o que está sendo retornado
          console.log('🔍 [EventPublicPage] Dados do evento recebidos:', {
            eventTitle: eventData.title,
            parishId: eventData.parishId,
            parishIdType: typeof eventData.parishId,
            parishIdIsObject: typeof eventData.parishId === 'object',
            parishName: eventData.parishId?.name,
            fullParishId: JSON.stringify(eventData.parishId)
          });
          
          // Extrair o nome da paróquia - pode vir como objeto populado ou como string/ID
          let parishName = 'Paróquia';
          if (eventData.parishId) {
            if (typeof eventData.parishId === 'object' && eventData.parishId !== null) {
              // Verificar se tem a propriedade name
              if (eventData.parishId.name) {
                parishName = eventData.parishId.name;
              } else if (eventData.parishId._id) {
                // Se for um objeto mas sem name, pode ser que o populate não funcionou
                console.warn('⚠️ [EventPublicPage] parishId é objeto mas não tem name:', eventData.parishId);
                parishName = 'Paróquia';
              }
            } else if (typeof eventData.parishId === 'string') {
              // Se for apenas o ID, não temos o nome aqui
              console.warn('⚠️ [EventPublicPage] parishId é apenas string/ID:', eventData.parishId);
              parishName = 'Paróquia';
            }
          } else {
            console.warn('⚠️ [EventPublicPage] parishId está vazio ou null');
          }
          
          const parishLogoUrl =
            typeof eventData.parishLogoUrl === 'string' && eventData.parishLogoUrl
              ? eventData.parishLogoUrl
              : typeof eventData.parishId === 'object' && eventData.parishId?.logoUrl
                ? eventData.parishId.logoUrl
                : undefined;

          const parishSlug =
            typeof eventData.parishSlug === 'string'
              ? eventData.parishSlug
              : typeof eventData.parishId === 'object' && eventData.parishId?.slug
                ? eventData.parishId.slug
                : undefined;

          const brandKeyRaw =
            typeof eventData.brandKey === 'string'
              ? eventData.brandKey
              : typeof eventData.parishId === 'object' && eventData.parishId?.brandKey
                ? eventData.parishId.brandKey
                : undefined;
          const brandKey =
            brandKeyRaw === 'zuadaria' || brandKeyRaw === 'platform' ? brandKeyRaw : undefined;

          const parsedEvent: EventData = {
            ...eventData,
            parishName: parishName,
            parishSlug,
            parishLogoUrl,
            brandKey,
          };

          console.log('✅ [EventPublicPage] Nome da paróquia extraído:', parishName);
          console.log('🎨 [EventPublicPage] Template da página:', eventData.pageTemplate || 'default');
          setEvent(parsedEvent);
        } else {
          setError('Não foi possível carregar os detalhes do evento');
        }
      } catch (err: any) {
        console.error('Erro ao buscar evento:', err);
        const exclusiveDomain = err.response?.data?.exclusiveDomain as string | undefined;
        if (exclusiveDomain) {
          replaceWithExclusiveDomain(exclusiveDomain);
          return;
        }
        if (err.response?.status === 404) {
          setError('Evento não encontrado ou não está disponível publicamente.');
        } else {
          setError('Erro ao carregar evento. Por favor, tente novamente.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventSlug, location.search]);

  // Carregar template dinamicamente apenas quando necessário (otimização de performance)
  useEffect(() => {
    if (pageTemplate === 'default' || !event) {
      setTemplateComponent(null);
      return;
    }

    setTemplateLoading(true);
    
    // Capitalizar primeira letra do template para corresponder ao nome do arquivo
    // Ex: 'acampamento' -> 'Acampamento', 'cinema' -> 'Cinema'
    const capitalizedTemplate = pageTemplate.charAt(0).toUpperCase() + pageTemplate.slice(1);
    
    // Dynamic import apenas quando necessário (melhor performance)
    import(`./templates/${capitalizedTemplate}EventPage`)
      .then((module) => {
        setTemplateComponent(() => module.default);
        setTemplateLoading(false);
      })
      .catch((error) => {
        console.error(`Erro ao carregar template ${pageTemplate}:`, error);
        setTemplateComponent(null); // Fallback para template padrão
        setTemplateLoading(false);
      });
  }, [pageTemplate, event]);

  // Efeito para alternar imagens automaticamente
  useEffect(() => {
    // No hero, usamos apenas coverImage.
    if (displayImages.length <= 1 || !isPlaying) return;
    
    const interval = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % displayImages.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [displayImages.length, isPlaying]);

  // Formatar data para exibição
  const formatEventDateRange = (startStr?: string, endStr?: string): string => {
    if (!startStr) return '';

    const start = new Date(startStr);
    const end = endStr ? new Date(endStr) : null;

    const sameDay = end && start.toDateString() === end.toDateString();

    if (!end || sameDay) {
      if (end && (start.getHours() !== end.getHours() || start.getMinutes() !== end.getMinutes())) {
        return `${format(start, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}, das ${format(start, 'HH:mm')} às ${format(end, 'HH:mm')}`;
      }
      return format(start, "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
    }

    const sameMonth = start.getMonth() === end.getMonth();
    const sameYear = start.getFullYear() === end.getFullYear();

    if (sameMonth && sameYear) {
      return `De ${format(start, "dd", { locale: ptBR })} a ${format(end, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}`;
    }

    return `De ${format(start, "dd 'de' MMMM", { locale: ptBR })} a ${format(end, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}`;
  };

  const formatHoursDuration = (start: Date, end: Date): string => {
    const totalMinutes = differenceInMinutes(end, start);
    if (totalMinutes <= 0) return '';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes} min`;
    if (minutes === 0) return hours === 1 ? '1 hora' : `${hours} horas`;
    return `${hours}h ${minutes}min`;
  };

  const getEventTimeLabel = (): string => {
    if (!event?.startDate || !event?.endDate) return '';
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    return `${format(start, 'HH:mm')} às ${format(end, 'HH:mm')}`;
  };

  const getEventScheduleSuffix = (): string => {
    if (!event?.startDate || !event?.endDate) return '';
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    const spanDays = differenceInCalendarDays(end, start) + 1;
    const totalMinutes = differenceInMinutes(end, start);
    const sameDay = spanDays <= 1 || totalMinutes < 24 * 60;
    if (sameDay) {
      const hours = formatHoursDuration(start, end);
      return hours ? `Duração: ${hours}` : '';
    }
    return `Acontece durante ${spanDays} dia${spanDays > 1 ? 's' : ''}`;
  };

  // Compartilhar evento
  const shareEvent = (platform: 'facebook' | 'twitter' | 'whatsapp'): void => {
    const eventUrl = window.location.href;
    const eventTitle = event?.title || 'Evento';
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Confira este evento: ${eventTitle}`)}&url=${encodeURIComponent(eventUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${eventTitle} - ${eventUrl}`)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  // Controles do slideshow
  const nextImage = () => {
    if (displayImages.length <= 0) return;
    setActiveImage((prev) => (prev + 1) % displayImages.length);
  };

  const prevImage = () => {
    if (displayImages.length <= 0) return;
    setActiveImage((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8, textAlign: exclusiveDomainUrl ? 'center' : undefined }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        {exclusiveDomainUrl ? (
          <Button href={exclusiveDomainUrl} variant="contained" target="_blank" rel="noreferrer">
            Abrir no domínio personalizado
          </Button>
        ) : (
          <Button component={Link} to="/" variant="contained">
            Voltar para a página inicial
          </Button>
        )}
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Evento não encontrado
        </Alert>
        <Button component={Link} to="/" variant="contained">
          Voltar para a página inicial
        </Button>
      </Container>
    );
  }

  // Renderizar template personalizado se carregado
  if (templateLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (TemplateComponent) {
    return withMetaPixelChrome(
      withCartChrome(
        <Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <CircularProgress />
          </Box>
        }>
          <TemplateComponent
            event={event}
            cartEnabled={cartEnabled}
            onAddTicketToCart={handleAddToCart}
          />
        </Suspense>
      )
    );
  }

  // Template padrão (default) - renderizado inline para melhor performance
  return withMetaPixelChrome(
    withCartChrome(
    <Box
      sx={{
        background: brand.flatPage
          ? brand.pageBg
          : `radial-gradient(circle at 0% 0%, ${safeAlpha(brand.pageBgDeep, 0.65)} 0%, transparent 42%), radial-gradient(circle at 100% 0%, ${safeAlpha(brand.accentSoft, 0.75)} 0%, transparent 38%), linear-gradient(180deg, ${brand.pageBg} 0%, ${brand.pageBgDeep} 100%)`,
        color: brand.text,
        minHeight: '100vh',
      }}
    >
      {/* HERO SECTION - Banner com altura ajustada (Modelo Eventbrite) */}
      <Box 
        sx={{ 
          display: 'flex',
          justifyContent: 'center',
          pt: { xs: 1, md: 1.5 },
          pb: 0,
          px: { xs: 1, md: 3 }
        }}
      >
        <Box 
          sx={{ 
            position: 'relative',
            height: { xs: '35vh', sm: '45vh', md: '60vh' },
            width: '100%',
            maxWidth: 'lg',
            overflow: 'hidden',
            borderRadius: { xs: 1.5, md: 3 }
          }}
        >
        {/* Background Images Slideshow */}
        {displayImages.length > 0 ? (
          displayImages.map((image, index) => (
            <Fade 
              key={index} 
              in={activeImage === index % displayImages.length} 
              timeout={1500}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: activeImage === index % displayImages.length ? 'block' : 'none'
              }}
            >
              <Box
                sx={{
                  backgroundImage: `url(${image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  width: '100%',
                  height: '100%',
                }}
              />
            </Fade>
          ))
        ) : (
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)',
              width: '100%',
              height: '100%',
            }}
          />
        )}

        {/* Overlay sutil para dar profundidade e escurecer a base */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)',
            zIndex: 1
          }}
        />

        {/* Controles do Slideshow */}
        {displayImages.length > 1 && (
          <>
            {/* Botões de navegação */}
            <IconButton
              onClick={prevImage}
              sx={{
                position: 'absolute',
                left: { xs: 8, md: 24 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 3,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                width: { xs: 32, md: 48 },
                height: { xs: 32, md: 48 },
                '&:hover': { 
                  bgcolor: 'rgba(0,0,0,0.8)',
                  transform: 'translateY(-50%) scale(1.1)'
                },
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease'
              }}
            >
              <ArrowBackIos sx={{ fontSize: { xs: '0.9rem', md: '1.2rem' } }} />
            </IconButton>
            
            <IconButton
              onClick={nextImage}
              sx={{
                position: 'absolute',
                right: { xs: 8, md: 24 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 3,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                width: { xs: 32, md: 48 },
                height: { xs: 32, md: 48 },
                '&:hover': { 
                  bgcolor: 'rgba(0,0,0,0.8)',
                  transform: 'translateY(-50%) scale(1.1)'
                },
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease'
              }}
            >
              <ArrowForwardIos sx={{ fontSize: { xs: '0.9rem', md: '1.2rem' } }} />
            </IconButton>

            {/* Controle Play/Pause */}
            <IconButton
              onClick={togglePlayPause}
              sx={{
                position: 'absolute',
                bottom: { xs: 55, md: 100 },
                right: { xs: 8, md: 24 },
                zIndex: 3,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                width: { xs: 32, md: 48 },
                height: { xs: 32, md: 48 },
                '&:hover': { 
                  bgcolor: 'rgba(0,0,0,0.8)',
                  transform: 'scale(1.1)'
                },
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease'
              }}
            >
              {isPlaying ? <Pause sx={{ fontSize: { xs: '1rem', md: '1.5rem' } }} /> : <PlayArrow sx={{ fontSize: { xs: '1rem', md: '1.5rem' } }} />}
            </IconButton>
          </>
        )}

        {/* Indicadores de slide melhorados */}
        {displayImages.length > 1 && (
          <Box 
            sx={{ 
              position: 'absolute',
              bottom: { xs: 12, md: 30 },
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex', 
              justifyContent: 'center', 
              gap: { xs: 1, md: 1.5 },
              zIndex: 3,
              bgcolor: 'rgba(0,0,0,0.4)',
              backdropFilter: 'blur(10px)',
              borderRadius: '25px',
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1.5 }
            }}
          >
            {displayImages.map((_, index) => (
              <Box 
                key={index}
                onClick={() => setActiveImage(index)}
                sx={{ 
                  width: activeImage === index ? { xs: 16, md: 24 } : { xs: 8, md: 12 }, 
                  height: { xs: 8, md: 12 }, 
                  borderRadius: '6px', 
                  bgcolor: activeImage === index ? '#FF512F' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: activeImage === index ? '#F09819' : 'rgba(255,255,255,0.8)'
                  }
                }} 
              />
            ))}
          </Box>
        )}
        </Box>
      </Box>
      
      {/* SEÇÃO DE INFORMAÇÕES DO EVENTO - Layout Eventbrite-like */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          pt: { xs: 1.5, md: 2 },
          pb: { xs: 1, md: 1.5 },
          px: { xs: 1, sm: 2, md: 3 },
          position: 'relative',
          zIndex: 2
        }}
      >
        {/* Card principal com informações do evento - Sem sobreposição negativa */}
        <Card 
          sx={{ 
            bgcolor: brand.flatPage ? brand.surface : safeAlpha(brand.surfaceSoft, 0.9), 
            backdropFilter: brand.flatPage ? 'none' : 'blur(20px)',
            borderRadius: { xs: 2, md: brand.flatPage ? 2.5 : 4 },
            border: `1px solid ${brand.border}`,
            boxShadow: brand.cardShadow,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 5 } }}>
            {/* Categoria */}
            {shouldShowEventCategoryTag(event.category) && (
            <Chip 
              label={event.category} 
              sx={{ 
                mb: { xs: 1.5, md: 2.5 }, 
                bgcolor: brand.flatPage ? 'rgba(26, 21, 21, 0.06)' : brand.accentSoft,
                color: brand.flatPage ? brand.textSoft : brand.accent,
                fontWeight: brand.flatPage ? 600 : 'bold',
                fontSize: { xs: '0.7rem', md: '0.8rem' },
                px: { xs: 1, md: 1.5 },
                py: 0.2,
                height: 'auto',
                boxShadow: 'none'
              }} 
            />
            )}
            
            {/* Título do evento */}
            <Typography 
              variant="h2" 
              component="h1" 
              sx={{ 
                fontWeight: brand.flatPage ? 800 : 900, 
                mb: { xs: 2, md: 4 }, 
                fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' },
                lineHeight: 1.2,
                color: brand.text,
                letterSpacing: '-0.02em',
                textShadow: brand.flatPage ? 'none' : '0 2px 14px rgba(43, 19, 75, 0.16)',
                textAlign: { xs: 'center', md: 'left' }
              }}
            >
              {eventDisplayTitle}
            </Typography>
            
            {/* Grid com informações e botão */}
            <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
              <Grid item xs={12} md={8}>
                {/* Informações do evento */}
                <Stack spacing={{ xs: 1.5, md: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    <CalendarToday sx={{ mr: 0.5, color: brand.flatPage ? brand.textSoft : brand.accent, fontSize: { xs: '1rem', md: '1.2rem' } }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.85rem', md: '1.05rem' },
                        color: brand.text
                      }}
                    >
                      {formatEventDateRange(event.startDate, event.endDate)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    <LocationOn sx={{ mr: 0.5, color: brand.flatPage ? brand.textSoft : brand.accent, fontSize: { xs: '1rem', md: '1.2rem' } }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.85rem', md: '1.05rem' },
                        color: brand.text
                      }}
                    >
                      {event.location} - {event.city}, {event.state}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    <AccessTime sx={{ mr: 0.5, color: brand.flatPage ? brand.textSoft : brand.accent, fontSize: { xs: '1rem', md: '1.2rem' } }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.85rem', md: '1.05rem' },
                        color: brand.text
                      }}
                    >
                      Horário: {getEventTimeLabel()}
                      {getEventScheduleSuffix() ? ` · ${getEventScheduleSuffix()}` : ''}
                    </Typography>
                  </Box>

                  {event.showRemainingSpots &&
                  typeof event.remainingSpots === 'number' &&
                  event.capacity > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <EventSeat sx={{ mr: 0.5, color: brand.flatPage ? brand.textSoft : brand.accent, fontSize: { xs: '1rem', md: '1.2rem' } }} />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '0.85rem', md: '1.05rem' },
                          color: brand.text,
                        }}
                      >
                        {event.remainingSpots <= 0
                          ? 'Vagas esgotadas'
                          : event.remainingSpots === 1
                            ? '1 vaga restante'
                            : `${event.remainingSpots.toLocaleString('pt-BR')} vagas restantes`}
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
              </Grid>
              
              <Grid item xs={12} md={4}>
                {/* Botões de ação */}
                <Stack spacing={{ xs: 1.5, md: 2 }} direction={{ xs: 'column', sm: 'row', md: 'column' }} alignItems="stretch">
                  {event.status === 'pre_published' ? (
                    <Button 
                      variant="contained" 
                      size="large"
                      disabled
                      sx={{ 
                        px: { xs: 2, md: 3 }, 
                        py: { xs: 1, md: 1.5 }, 
                        fontSize: { xs: '0.85rem', md: '0.95rem' },
                        fontWeight: 'bold',
                        borderRadius: '50px',
                        background: 'linear-gradient(45deg, #9E9E9E 0%, #757575 100%)',
                        boxShadow: '0 6px 30px rgba(158, 158, 158, 0.4)',
                        cursor: 'not-allowed',
                        textTransform: 'none',
                        minHeight: { xs: 40, md: 48 }
                      }}
                      startIcon={<EventSeat sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                    >
                      Evento não disponível ainda
                    </Button>
                  ) : eventHasEnded ? (
                    <Button 
                      variant="contained" 
                      size="large"
                      disabled
                      sx={{ 
                        px: { xs: 2, md: 3 }, 
                        py: { xs: 1, md: 1.5 }, 
                        fontSize: { xs: '0.85rem', md: '0.95rem' },
                        fontWeight: 'bold',
                        borderRadius: '50px',
                        background: '#eceff5',
                        color: '#6b7280',
                        boxShadow: '0 6px 30px rgba(0,0,0,0.4)',
                        cursor: 'not-allowed',
                        textTransform: 'none',
                        minHeight: { xs: 40, md: 48 }
                      }}
                      startIcon={<EventSeat sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                    >
                      Evento encerrado
                    </Button>
                  ) : event.isSoldOut ? (
                    <Button 
                      variant="contained" 
                      size="large"
                      disabled
                      sx={{ 
                        px: { xs: 2, md: 3 }, 
                        py: { xs: 1, md: 1.5 }, 
                        fontSize: { xs: '0.85rem', md: '0.95rem' },
                        fontWeight: 'bold',
                        borderRadius: '50px',
                        background: 'linear-gradient(45deg, #f44336 0%, #d32f2f 100%)',
                        color: 'white',
                        boxShadow: '0 6px 30px rgba(244, 67, 54, 0.4)',
                        cursor: 'not-allowed',
                        textTransform: 'none',
                        minHeight: { xs: 40, md: 48 }
                      }}
                      startIcon={<EventSeat sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                    >
                      Evento Esgotado
                    </Button>
                  ) : !hasAvailableTickets ? (
                    <Button 
                      variant="contained" 
                      size="large"
                      disabled
                      sx={{ 
                        px: { xs: 2, md: 3 }, 
                        py: { xs: 1, md: 1.5 }, 
                        fontSize: { xs: '0.85rem', md: '0.95rem' },
                        fontWeight: 'bold',
                        borderRadius: '50px',
                        background: 'rgba(244, 67, 54, 0.12)',
                        color: '#f44336',
                        boxShadow: '0 6px 30px rgba(244, 67, 54, 0.3)',
                        cursor: 'not-allowed',
                        textTransform: 'none',
                        minHeight: { xs: 40, md: 48 }
                      }}
                      startIcon={<EventSeat sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                    >
                      Vendas encerradas
                    </Button>
                  ) : (
                    <Button 
                      variant="contained" 
                      size="large"
                      sx={{ 
                        px: { xs: 2, md: 3 }, 
                        py: { xs: 1, md: 1.5 }, 
                        fontSize: { xs: '0.85rem', md: '0.95rem' },
                        fontWeight: 'bold',
                        borderRadius: '50px',
                        background: `linear-gradient(135deg, ${brand.ctaStart} 0%, ${brand.ctaEnd} 100%)`,
                        boxShadow: `0 8px 24px ${brand.ctaShadow}`,
                        textTransform: 'none',
                        minHeight: { xs: 40, md: 48 },
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: `linear-gradient(135deg, ${brand.ctaEnd} 0%, ${brand.ctaStart} 100%)`,
                          transform: 'translateY(-2px)',
                          boxShadow: `0 12px 28px ${brand.ctaShadow}`,
                        }
                      }}
                      {...(cartEnabled
                        ? {
                            onClick: () => document.getElementById('ingressos')?.scrollIntoView({ behavior: 'smooth' }),
                          }
                        : {
                            component: Link,
                            to: `/${eventSlug}/inscricao`,
                          })}
                      startIcon={<EventSeat sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                    >
                      {event.isPaid ? 'Comprar Ingresso' : 'Inscrever-se'}
                    </Button>
                  )}
                  
                  {/* Botão de Compartilhar */}
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={() => shareEvent('whatsapp')}
                    sx={{ 
                      px: { xs: 2, md: 3 }, 
                      py: { xs: 1, md: 1.5 }, 
                      fontSize: { xs: '0.8rem', md: '0.9rem' },
                      fontWeight: 'bold',
                      borderRadius: '50px',
                      color: brand.text, 
                      borderColor: brand.border,
                      textTransform: 'none',
                      minHeight: { xs: 40, md: 48 },
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: brand.accent,
                        color: brand.accent,
                        bgcolor: safeAlpha(brand.accent, 0.05),
                        transform: 'translateY(-1px)'
                      }
                    }}
                    startIcon={<Share sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                  >
                    Compartilhar
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
      
      {/* Conteúdo principal */}
      <Container maxWidth="lg" sx={{ pt: { xs: 1, md: 2 }, pb: { xs: 6, md: 8 } }}>
        <Grid container spacing={6}>
          {/* Coluna principal */}
          <Grid item xs={12} md={8}>
            {/* Descrição do evento */}
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 'bold', color: brand.heading }}>
                Sobre este evento
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line', fontSize: '1.1rem', lineHeight: 1.8, color: brand.textSoft }}>
                {event.description}
              </Typography>
            </Box>
            
            {/* Galeria de imagens */}
            {event.galleryImages && event.galleryImages.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 'bold', color: brand.heading }}>
                  Galeria
                </Typography>
                <ImageList 
                  variant="quilted" 
                  cols={isMobile ? 2 : 3} 
                  gap={16}
                  sx={{ 
                    overflow: 'hidden',
                    borderRadius: 2,
                    '& .MuiImageListItem-root': {
                      overflow: 'hidden'
                    }
                  }}
                >
                  {event.galleryImages.map((image, index) => (
                    <ImageListItem 
                      key={index} 
                      cols={index % 3 === 0 ? 2 : 1} 
                      rows={index % 3 === 0 ? 2 : 1}
                    >
                      <Box
                        component="img"
                        src={toBrowserImageUrl(image)}
                        alt={`Imagem ${index + 1} do evento`}
                        loading="lazy"
                        sx={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          transition: 'transform 0.5s ease',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </Box>
            )}
            
            {/* Localização */}
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 'bold', color: brand.heading }}>
                Localização
              </Typography>
              
              <Card
                sx={{
                  bgcolor: brand.surfaceSoft,
                  color: brand.text,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${brand.border}`,
                  boxShadow: brand.cardShadow,
                  '& .MuiCardContent-root': { bgcolor: 'transparent' }
                }}
              >
                <Grid container>
                  <Grid item xs={12} md={6}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {event.location}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        {event.address}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 3 }}>
                        {event.city}, {event.state}
                      </Typography>
                      
                      <Button 
                        variant="outlined" 
                        startIcon={<DirectionsBus />}
                        href={event.mapLink || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${event.location} ${event.address} ${event.city} ${event.state}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: brand.text, 
                          borderColor: brand.border,
                          '&:hover': {
                            borderColor: brand.accent,
                            color: brand.accent
                          }
                        }}
                      >
                        Como chegar
                      </Button>
                    </CardContent>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    {/* Mapa (placeholder) */}
                    <Box 
                      sx={{ 
                        height: '100%',
                        minHeight: 200,
                        bgcolor: safeAlpha(brand.accent, 0.08), 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center'
                      }}
                    >
                      {mapEmbedSrc ? (
                        <iframe
                          title={`Mapa de localização - ${event.location}`}
                          src={mapEmbedSrc}
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      ) : (
                        <Typography variant="body2" color={brand.textSoft}>
                          Mapa do local do evento
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Box>
            
            {/* FAQ */}
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 'bold', color: brand.heading }}>
                Perguntas Frequentes
              </Typography>
              
              <Box sx={{ '& .MuiPaper-root': { bgcolor: brand.surfaceSoft, color: brand.text, mb: 2, border: `1px solid ${brand.border}`, boxShadow: '0 10px 24px rgba(45, 24, 78, 0.10)' } }}>
                <Accordion sx={{ borderRadius: '8px !important', overflow: 'hidden' }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMore sx={{ color: brand.text }} />}
                    sx={{ borderBottom: `1px solid ${brand.border}` }}
                  >
                    <Typography variant="subtitle1" fontWeight="medium">
                      Posso transferir meu ingresso para outra pessoa?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1">
                      Entre em contato com o organizador do evento para verificar a possibilidade de transferência de ingressos.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                
                <Accordion sx={{ borderRadius: '8px !important', overflow: 'hidden' }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMore sx={{ color: brand.text }} />}
                    sx={{ borderBottom: `1px solid ${brand.border}` }}
                  >
                    <Typography variant="subtitle1" fontWeight="medium">
                      Qual a política de cancelamento?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" component="div">
                      <strong>Regra principal:</strong> Você tem até 7 dias corridos, contados a partir da data da sua compra, para solicitar o cancelamento.
                      <br /><br />
                      <strong>Evento próximo?</strong> O prazo mínimo é de 48 horas antes do início do evento para solicitar cancelamento com reembolso total. Após esse período, não haverá reembolso.
                      <br /><br />
                      <strong>Como solicitar reembolso:</strong>{' '}
                      {event?.personalizedPage
                        ? 'Entre em contato com o organizador do evento pelo canal de suporte informado na página.'
                        : (
                          <>
                            Abra um pedido pelo botão abaixo. Você informa o motivo e o e-mail da compra,
                            recebe um código de confirmação e, após validar, nosso suporte é notificado.
                            <Box sx={{ mt: 1.5 }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => setCancellationTicketOpen(true)}
                                sx={{ textTransform: 'none', fontWeight: 700 }}
                              >
                                Abrir pedido de reembolso
                              </Button>
                            </Box>
                          </>
                        )}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                
                <Accordion sx={{ borderRadius: '8px !important', overflow: 'hidden' }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMore sx={{ color: brand.text }} />}
                    sx={{ borderBottom: `1px solid ${brand.border}` }}
                  >
                    <Typography variant="subtitle1" fontWeight="medium">
                      Não recebi o email de confirmação, o que fazer?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Se você não recebeu o email de confirmação com seu ingresso, não se preocupe! 
                      Você pode solicitar o reenvio do email de forma rápida e fácil.
                    </Typography>
                    <Button
                      variant="contained"
                      href={`/${eventSlug}/suporte-email`}
                      sx={{
                        background: `linear-gradient(135deg, ${brand.ctaStart} 0%, ${brand.ctaEnd} 100%)`,
                        '&:hover': { background: `linear-gradient(135deg, ${brand.ctaEnd} 0%, ${brand.ctaStart} 100%)` },
                        textTransform: 'none',
                        fontWeight: 'bold',
                        px: 3,
                        py: 1
                      }}
                    >
                      Reenviar Email de Confirmação
                    </Button>
                    <Typography variant="body2" sx={{ mt: 2, color: brand.textSoft }}>
                      💡 Dica: Verifique também sua pasta de spam/lixo eletrônico!
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Box>
          </Grid>
          
          {/* Coluna lateral */}
          <Grid item xs={12} md={4}>
            <Card
              id="ingressos"
              sx={{ 
              bgcolor: brand.surfaceSoft, 
              color: brand.text, 
              borderRadius: 2, 
              mb: 4,
              border: `1px solid ${brand.border}`,
              boxShadow: brand.cardShadow,
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 1 }}>
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', color: brand.heading }}>
                    Ingressos
                  </Typography>
                </Box>

                {event.ticketCatalogMode === 'phased' && event.activeSalesPhase && (
                  <ActiveLotBanner
                    phase={event.activeSalesPhase}
                    compact
                    accentColor={brand.accent}
                  />
                )}
                
                {event.tickets && event.tickets.length > 0 ? (
                  <Box>
                    {event.tickets.map((ticket) => {
                      // Verificar o estado deste ingresso específico
                      const now = new Date();
                      const ticketStartSaleDate = ticket.startSaleDate ? new Date(ticket.startSaleDate) : null;
                      const ticketEndSaleDate = ticket.endSaleDate ? new Date(ticket.endSaleDate) : null;
                      const hasStock = ticket.quantity > 0;
                      const isPhasedCatalog = event.ticketCatalogMode === 'phased';
                      
                      // No modo phased, ingressos listados já vêm do lote ativo — não bloquear por startSaleDate legado
                      const saleStarted =
                        isPhasedCatalog || !ticketStartSaleDate || ticketStartSaleDate <= now;
                      const saleNotEnded = !ticketEndSaleDate || ticketEndSaleDate >= now;
                      
                      // Determinar o estado do ingresso
                      const isExpired = ticketEndSaleDate && ticketEndSaleDate < now;
                      const isOutOfStock = !hasStock;
                      const isTicketAvailable =
                        !eventHasEnded && !event.isSoldOut && saleStarted && saleNotEnded && hasStock;

                      return (
                        <Box 
                          key={ticket._id} 
                          sx={{ 
                            py: 2, 
                            borderBottom: `1px solid ${brand.border}`,
                            transition: 'opacity 0.3s ease',
                            opacity: isTicketAvailable ? 1 : 0.6,
                            '&:last-child': { borderBottom: 'none', pb: 0 },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <Typography variant="h6">
                              {ticket.name}
                            </Typography>
                            {getTicketLotName(ticket) && event.ticketCatalogMode === 'batched' && (
                              <Chip
                                label={getTicketLotName(ticket)}
                                size="small"
                                sx={{ fontWeight: 700, bgcolor: brand.accentSoft, color: brand.accent }}
                              />
                            )}
                            {isZuadariaPublic && ticket.lastTickets && isTicketAvailable ? (
                              <Chip
                                label="Últimos ingressos"
                                size="small"
                                sx={{
                                  fontWeight: 800,
                                  bgcolor: '#F2B33D',
                                  color: '#1A1515',
                                }}
                              />
                            ) : null}
                          </Box>
                          {(() => {
                            const safeDescription = getSafeTicketDescription(ticket);
                            return safeDescription ? (
                              <Typography variant="body2" sx={{ mb: 2, color: brand.textSoft }}>
                                {safeDescription}
                              </Typography>
                            ) : null;
                          })()}
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Box>
                              {ticket.showPlatformFee && ticket.price > 0 ? (
                                (() => {
                                  const ticketTotals = calculateTicketTotalsWithFee({
                                    unitPrice: ticket.price,
                                    quantity: 1,
                                    feePercentage: event.feePercentage,
                                    showPlatformFee: ticket.showPlatformFee,
                                  });
                                  return (
                                <>
                                  <Typography variant="h6" sx={{ color: brand.heading, fontWeight: 'bold' }}>
                                    R$ {ticketTotals.total.toFixed(2)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: brand.textSoft, display: 'block' }}>
                                    Ingresso: R$ {ticket.price.toFixed(2)} + Taxa: R$ {ticketTotals.feeAmount.toFixed(2)}
                                  </Typography>
                                </>
                                  );
                                })()
                              ) : (
                                <Typography variant="h6" sx={{ color: brand.heading, fontWeight: 'bold' }}>
                                  {ticket.price > 0 ? `R$ ${ticket.price.toFixed(2)}` : 'Gratuito'}
                                </Typography>
                              )}
                              {typeof ticket.quantity === 'number' &&
                              (event.showRemainingSpots || ticket.quantity <= 0) ? (
                                <Typography variant="caption" sx={{ color: brand.textSoft, display: 'block', mt: 0.5 }}>
                                  {ticket.quantity <= 0
                                    ? 'Esgotado'
                                    : ticket.quantity === 1
                                      ? '1 disponível'
                                      : `${ticket.quantity.toLocaleString('pt-BR')} disponíveis`}
                                </Typography>
                              ) : null}
                            </Box>
                            
                            {isTicketAvailable ? (
                              cartEnabled ? (
                                (() => {
                                  const qty = cartItems.find((item) => item.ticketId === String(ticket._id))?.quantity || 0;
                                  const { min: minPerOrder, max: maxPerOrder } = getTicketOrderLimits(ticket);
                                  const otherPeople = cartPeople - qty;
                                  const roomLeft = Math.max(0, MAX_CART_ATTENDEES - otherPeople);
                                  const effectiveMax = Math.min(maxPerOrder, roomLeft, Math.max(1, ticket.quantity));
                                  const canIncrease = qty < effectiveMax;
                                  return (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    bgcolor: brand.surface,
                                    borderRadius: 999,
                                    border: `1px solid ${brand.border}`,
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    disabled={qty <= 0}
                                    onClick={() => handleChangeTicketQty(ticket, qty <= minPerOrder ? 0 : qty - 1)}
                                    aria-label={`Diminuir ${ticket.name}`}
                                    sx={{ color: brand.accent, '&.Mui-disabled': { color: brand.border } }}
                                  >
                                    <Remove fontSize="small" />
                                  </IconButton>
                                  <Typography sx={{ minWidth: 20, textAlign: 'center', fontWeight: 700 }}>
                                    {qty}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    disabled={!canIncrease}
                                    onClick={() =>
                                      handleChangeTicketQty(
                                        ticket,
                                        qty === 0 ? minPerOrder : Math.min(effectiveMax, qty + 1)
                                      )
                                    }
                                    aria-label={`Aumentar ${ticket.name}`}
                                    sx={{ color: brand.accent, '&.Mui-disabled': { color: brand.border } }}
                                  >
                                    <Add fontSize="small" />
                                  </IconButton>
                                </Box>
                                  );
                                })()
                              ) : (
                              <Button 
                                variant="contained"
                                component={Link}
                                to={`/${eventSlug}/inscricao?ticket=${ticket._id}`}
                                sx={{ 
                                  borderRadius: '50px',
                                  background: `linear-gradient(135deg, ${brand.ctaStart} 0%, ${brand.ctaEnd} 100%)`,
                                  boxShadow: `0 4px 16px ${brand.ctaShadow}`,
                                  '&:hover': {
                                    background: `linear-gradient(135deg, ${brand.ctaEnd} 0%, ${brand.ctaStart} 100%)`,
                                  }
                                }}
                              >
                                Selecionar
                              </Button>
                              )
                            ) : (
                              <Box sx={{ textAlign: 'right' }}>
                                {isExpired ? (
                                  <>
                                    <Chip 
                                      label="Vendas Encerradas" 
                                      size="small" 
                                      sx={{ 
                                        bgcolor: 'rgba(244, 67, 54, 0.2)', 
                                        color: '#f44336',
                                        fontWeight: 'bold',
                                        mb: 1
                                      }} 
                                    />
                                    <Typography variant="caption" sx={{ display: 'block', color: brand.textSoft }}>
                                      Encerrado em {new Date(ticket.endSaleDate!).toLocaleDateString('pt-BR')}
                                    </Typography>
                                  </>
                                ) : isOutOfStock ? (
                                  <Chip 
                                    label="Esgotado" 
                                    size="small" 
                                    sx={{ 
                                      bgcolor: 'rgba(244, 67, 54, 0.2)', 
                                      color: '#f44336',
                                      fontWeight: 'bold'
                                    }} 
                                  />
                                ) : eventHasEnded ? (
                                  <Button 
                                    variant="contained"
                                    disabled
                                    sx={{ 
                                      borderRadius: '50px',
                                      background: 'linear-gradient(45deg, #9E9E9E 0%, #757575 100%)',
                                      boxShadow: 'none',
                                      cursor: 'not-allowed',
                                      textTransform: 'none'
                                    }}
                                  >
                                    Evento encerrado
                                  </Button>
                                ) : event.isSoldOut ? (
                                  <Chip 
                                    label="Evento Esgotado" 
                                    size="small" 
                                    sx={{ 
                                      bgcolor: 'rgba(244, 67, 54, 0.2)', 
                                      color: '#f44336',
                                      fontWeight: 'bold'
                                    }} 
                                  />
                                ) : (
                                  <Button 
                                    variant="contained"
                                    disabled
                                    sx={{ 
                                      borderRadius: '50px',
                                      background: 'linear-gradient(45deg, #9E9E9E 0%, #757575 100%)',
                                      boxShadow: 'none',
                                      cursor: 'not-allowed',
                                      textTransform: 'none'
                                    }}
                                  >
                                    Indisponível
                                  </Button>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body1" color={brand.textSoft} sx={{ textAlign: 'center', py: 2 }}>
                    Nenhum ingresso disponível para este evento.
                  </Typography>
                )}
              </CardContent>
              {cartEnabled && (
                <Box sx={{ px: 3, pb: 3, pt: 2, borderTop: `1px solid ${brand.border}`, bgcolor: brand.surfaceSoft }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5, gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: brand.text }}>Total</Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontWeight: 800, color: brand.accent }}>
                        R$ {cartSubtotal.toFixed(2).replace('.', ',')}
                      </Typography>
                      {(event.allowInstallments || event.tickets?.some((ticket) => ticket.allowInstallments)) && cartSubtotal > 0 && (
                        <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, display: 'block' }}>
                          ou 12x R$ {(cartSubtotal / 12).toFixed(2).replace('.', ',')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={cartPeople < 1}
                    onClick={goToCartCheckout}
                    sx={{
                      py: 1.4,
                      borderRadius: '50px',
                      fontWeight: 700,
                      textTransform: 'none',
                      background: `linear-gradient(135deg, ${brand.ctaStart} 0%, ${brand.ctaEnd} 100%)`,
                      boxShadow: `0 4px 16px ${brand.ctaShadow}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${brand.ctaEnd} 0%, ${brand.ctaStart} 100%)`,
                      },
                      '&.Mui-disabled': {
                        background: brand.border,
                        color: brand.textSoft,
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {cartPeople > 0 && (
                      <Box
                        component="span"
                        sx={{
                          width: 22,
                          height: 22,
                          mr: 1,
                          borderRadius: '50%',
                          bgcolor: 'rgba(255,255,255,0.22)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.8rem',
                          fontWeight: 800,
                        }}
                      >
                        {cartPeople}
                      </Box>
                    )}
                    {event.isPaid ? 'Comprar ingressos' : 'Inscrever-se'}
                  </Button>
                </Box>
              )}
            </Card>
            
            {/* Loja */}
            {event.hasStore && event.products && event.products.length > 0 && (
              <Card sx={{ 
                bgcolor: brand.surface, 
                color: brand.text, 
                borderRadius: 2, 
                mb: 4,
                border: `1px solid ${brand.border}`,
                boxShadow: '0 14px 30px rgba(45, 24, 78, 0.14)',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: brand.heading }}>
                    🛒 Loja do Evento
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: brand.textSoft, mb: 2 }}>
                      Adquira nossos produtos especiais para complementar sua experiência no evento:
                    </Typography>
                    
                    {event.products.map((product) => (
                      <Box key={product._id} sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 2, 
                        p: 2, 
                        bgcolor: safeAlpha(brand.accent, 0.06), 
                        borderRadius: 1 
                      }}>
                        <Box sx={{ 
                          width: 60, 
                          height: 80, 
                          bgcolor: brand.accent, 
                          borderRadius: 1, 
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.8rem',
                          overflow: 'hidden'
                        }}>
                          {product.image ? (
                            <img 
                              src={product.image} 
                              alt={product.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            'PRODUTO'
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {product.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: brand.textSoft, mb: 1 }}>
                            {product.description}
                          </Typography>
                          <Typography variant="h6" sx={{ color: brand.heading, fontWeight: 'bold' }}>
                            R$ {product.price.toFixed(2).replace('.', ',')}
                          </Typography>
                          {product.quantity > 0 && (
                            <Typography variant="caption" sx={{ color: brand.textSoft }}>
                              {product.quantity} unidades disponíveis
                            </Typography>
                          )}
                        </Box>
                        <Button 
                          variant="contained"
                          size="small"
                          component={Link}
                          to={`/${eventSlug}/produto/${product._id}`}
                          disabled={product.quantity <= 0}
                          sx={{ 
                            bgcolor: brand.accent,
                            '&:hover': { bgcolor: '#3d1564' },
                            '&:disabled': { bgcolor: 'rgba(255,255,255,0.1)' }
                          }}
                        >
                          {product.quantity > 0 ? 'Comprar' : 'Esgotado'}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                  
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    * Produtos disponíveis para retirada no local do evento
                  </Typography>
                </CardContent>
              </Card>
            )}
            
            {/* Organizador */}
            <Card sx={{ 
              bgcolor: brand.surface, 
              color: brand.text, 
              borderRadius: 2, 
              mb: 4,
              border: `1px solid ${brand.border}`,
              boxShadow: '0 14px 30px rgba(45, 24, 78, 0.14)',
            }}>
              <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: brand.heading }}>
                  Organizado por
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    src={event.parishLogoUrl || undefined}
                    alt={organizerDisplayName || 'Organizador'}
                    sx={{
                      width: 70,
                      height: 70,
                      mr: 2,
                      bgcolor: safeAlpha(brand.accent, 0.08),
                      color: brand.accent,
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      '& img': { objectFit: 'contain', p: 0.5 },
                    }}
                  >
                    {organizerDisplayName ? organizerDisplayName.charAt(0) : 'P'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {organizerDisplayName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: brand.textSoft }}>
                      Organizador de eventos
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            
            {/* Compartilhar */}
            <Card sx={{ bgcolor: brand.surfaceSoft, color: brand.text, borderRadius: 2, border: `1px solid ${brand.border}`, boxShadow: '0 14px 30px rgba(45, 24, 78, 0.14)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: brand.heading }}>
                  Compartilhar
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<Facebook />}
                    onClick={() => shareEvent('facebook')}
                    sx={{ 
                      flex: 1,
                      color: brand.text, 
                      borderColor: brand.border,
                      '&:hover': {
                        borderColor: brand.accent,
                        color: brand.accent
                      }
                    }}
                  >
                    Facebook
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<Twitter />}
                    onClick={() => shareEvent('twitter')}
                    sx={{ 
                      flex: 1,
                      color: brand.text, 
                      borderColor: brand.border,
                      '&:hover': {
                        borderColor: brand.accent,
                        color: brand.accent
                      }
                    }}
                  >
                    Twitter
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<WhatsApp />}
                    onClick={() => shareEvent('whatsapp')}
                    sx={{ 
                      flex: 1,
                      color: brand.text, 
                      borderColor: brand.border,
                      '&:hover': {
                        borderColor: brand.accent,
                        color: brand.accent
                      }
                    }}
                  >
                    WhatsApp
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {!event?.personalizedPage ? (
        <CancellationTicketDialog
          open={cancellationTicketOpen}
          onClose={() => setCancellationTicketOpen(false)}
          eventId={event?._id}
          eventSlug={event?.slug || eventSlug}
          eventTitle={event?.title}
        />
      ) : null}
    </Box>
    )
  );
};

export default EventPublicPage;